'use client';

import { Suspense, useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

/**
 * Enterprise-grade global route-transition progress bar.
 *
 * Phase 1 of the loading-UX overhaul. Before this, clicking a nav link produced
 * no visual feedback while the server resolved auth + layout + module gates —
 * the "frozen white screen". This bar gives an instant signal for EVERY in-app
 * navigation regardless of how it was triggered:
 *   - Next <Link> clicks (sidebar) and InternalNavigationHandler-intercepted <a>
 *     clicks are caught by a capture-phase document click listener (fires before
 *     either handler runs).
 *   - Programmatic router.push() is caught by patching history.pushState.
 *   - Browser back/forward is caught via popstate.
 * Completion fires when the route commits (pathname/search change).
 *
 * CSP-safe: no injected <style> tag and no parsed style="" attribute — the only
 * dynamic style is the bar width, set by React via CSSOM, which strict CSP
 * (style-src 'self' + nonce) does not block.
 */

const TRICKLE_INTERVAL_MS = 250;
const COMPLETE_HOLD_MS = 220;
// Auto-complete if a navigation somehow never settles (defends against a stuck
// bar). Generous so genuinely slow pages still complete naturally first.
const SAFETY_CEILING_MS = 12_000;
const CLIENT_OWNED_NAVIGATION_CANCEL_EVENT = 'statura:route-progress-cancel';

function RouteProgressBarInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();

  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  const activeRef = useRef(false);
  const trickleRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const safetyRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // start/done held in refs so the once-bound listeners always call the latest.
  const startRef = useRef<() => void>(() => {});
  const doneRef = useRef<() => void>(() => {});
  // Last committed route URL (pathname + search), so popstate can tell a real
  // route change from a hash-only back/forward (which would otherwise start a
  // bar that never completes until the safety timeout).
  const lastUrlRef = useRef('');

  useEffect(() => {
    function clearProgressTimers() {
      if (trickleRef.current) clearInterval(trickleRef.current);
      if (safetyRef.current) clearTimeout(safetyRef.current);
      trickleRef.current = null;
      safetyRef.current = null;
    }

    function start() {
      if (activeRef.current) return;
      activeRef.current = true;
      if (hideRef.current) clearTimeout(hideRef.current);
      setVisible(true);
      setProgress(8);
      clearProgressTimers();
      // Ease-out trickle: fast at first, asymptotes toward 90% so it never
      // "finishes" until the route actually commits.
      trickleRef.current = setInterval(() => {
        setProgress((p) => (p >= 90 ? p : p + Math.max(0.4, (90 - p) * 0.1)));
      }, TRICKLE_INTERVAL_MS);
      safetyRef.current = setTimeout(() => doneRef.current(), SAFETY_CEILING_MS);
    }

    function done() {
      if (!activeRef.current) return;
      activeRef.current = false;
      clearProgressTimers();
      setProgress(100);
      hideRef.current = setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, COMPLETE_HOLD_MS);
    }

    function cancel() {
      activeRef.current = false;
      clearProgressTimers();
      if (hideRef.current) clearTimeout(hideRef.current);
      hideRef.current = null;
      setVisible(false);
      setProgress(0);
    }

    startRef.current = start;
    doneRef.current = done;

    function isMeaningfulNav(target: URL, current: Location): boolean {
      if (target.origin !== current.origin) return false;
      // Pure hash change on the same page is not a route transition.
      return target.pathname !== current.pathname || target.search !== current.search;
    }

    // --- START: programmatic navigations (router.push) ---
    const origPush = history.pushState.bind(history);
    history.pushState = function patchedPushState(...args: Parameters<History['pushState']>): void {
      try {
        const url = args[2];
        if (url != null) {
          const target = new URL(url.toString(), window.location.href);
          if (isMeaningfulNav(target, window.location)) start();
        }
      } catch {
        /* ignore malformed URL — never block the real navigation */
      }
      return origPush(...args);
    };

    // --- START: browser back / forward ---
    function onPopState() {
      // Only a real route change (pathname/search) starts the bar. A hash-only
      // back/forward leaves pathname/search unchanged, so the completion effect
      // would never fire — don't start a bar we can't naturally complete.
      const current = `${window.location.pathname}${window.location.search}`;
      if (current !== lastUrlRef.current) start();
    }
    window.addEventListener('popstate', onPopState);

    // --- START: any anchor click (Link or intercepted <a>) ---
    function onClick(event: MouseEvent) {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const el = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!(el instanceof HTMLAnchorElement)) return;
      if (el.dataset.routeProgress === 'ignore') return;
      if (el.target && el.target !== '_self') return;
      if (el.hasAttribute('download')) return;
      if (el.dataset.hardNavigation === 'true') return;
      try {
        const target = new URL(el.href, window.location.origin);
        if (isMeaningfulNav(target, window.location)) start();
      } catch {
        /* ignore */
      }
    }
    document.addEventListener('click', onClick, true);
    window.addEventListener(CLIENT_OWNED_NAVIGATION_CANCEL_EVENT, cancel);

    return () => {
      history.pushState = origPush;
      window.removeEventListener('popstate', onPopState);
      document.removeEventListener('click', onClick, true);
      window.removeEventListener(CLIENT_OWNED_NAVIGATION_CANCEL_EVENT, cancel);
      clearProgressTimers();
      if (hideRef.current) clearTimeout(hideRef.current);
    };
  }, []);

  // COMPLETE: the route committed and the new segment rendered. pathname/search
  // are the trigger — the body only calls the latest done() via a ref. Record
  // the committed URL (matching window.location's pathname+search format) so
  // popstate can distinguish a real route change from a hash-only one.
  useEffect(() => {
    lastUrlRef.current = `${pathname}${search ? `?${search}` : ''}`;
    doneRef.current();
  }, [pathname, search]);

  if (!visible) return null;

  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-x-0 top-0 z-[200] h-[3px]">
      <div
        className="h-full rounded-r-full bg-statura-green-accessible shadow-[0_0_10px_1px_rgba(62,93,74,0.55)] transition-[width,opacity] duration-200 ease-out"
        style={{ width: `${progress}%`, opacity: progress >= 100 ? 0 : 1 }}
      />
    </div>
  );
}

/**
 * useSearchParams() opts a component into client-side rendering of the search
 * params; wrapping in Suspense keeps it from forcing a static-render bailout on
 * any ancestor that might be statically rendered.
 */
export function RouteProgressBar() {
  return (
    <Suspense fallback={null}>
      <RouteProgressBarInner />
    </Suspense>
  );
}
